/**
 * Basemap — peta dasar lapangan bergaya layar Field Map, dipakai di layar
 * korelasi.
 *
 * Peta lintasan sebelumnya hanya menggambar kepala sumur dan garis A–A′. Yang
 * kurang darinya adalah konteks struktural: sebuah lintasan bisa menyeberang
 * tinggian atau menyusuri sayapnya, dan dua urutan panel yang tampak sama di
 * atas kertas menjawab dua pertanyaan geologi yang berbeda. Maka peta ini
 * membawa kontur struktur, jalur lubang bor, dan simbol status ke sisi lembar
 * korelasi.
 *
 * Isinya adalah sumur yang tercentang di daftar "Wells correlated": itulah
 * yang diberi warna panel, label, dan garis lintasan. Sumur yang tidak
 * tercentang tetap digambar sebagai titik samar — tanpanya bentuk lapangan
 * hilang begitu separuh sumur dimatikan — dan tetap bisa diklik untuk
 * memasukkannya ke korelasi.
 *
 * Tiga hal menjawab pertanyaan yang datang **setelah** bentuknya terbaca, dan
 * ketiganya menunjuk satu titik tertentu alih-alih seluruh lapangan. Sumur
 * samar ikut bernama, menurut ruang yang tersisa di layar, sehingga
 * memperbesar memulangkan namanya satu per satu. Kisi koordinat menyebut
 * tempat di tepinya, dengan langkah yang dihitung ulang dari bingkai yang
 * sedang tampak — angka yang berubah sendiri ketika peta diperbesar. Dan sumur
 * yang ditunjuk kursor menyala penuh serta membawa keterangannya sendiri —
 * nama, apakah ia dikorelasikan, statusnya, dan koordinatnya — sebab nama
 * setinggi delapan piksel yang berdesakan dengan label kontur adalah nama yang
 * harus dieja orang satu aksara demi satu aksara.
 */

import { useCallback, useMemo, useState } from "react";

import { useElementSize, useMapView } from "../hooks";
import {
  WELL_STATUS_LEGEND,
  applyView,
  contourAnchor,
  extentOf,
  fitTransform,
  structureContours,
  structureControlPoints,
  traverseEnds,
  type Extent,
  type MapTransform,
} from "../lib/basemap";
import { coordinateStep, coordinateTicks } from "../lib/ticks";
import { unitsFor } from "../lib/units";
import MapZoomControls from "./MapZoomControls";
import { Chip } from "./ui";

export interface BasemapWell {
  id: number;
  name: string;
  x: number | null;
  y: number | null;
  /** Warna simbol menurut status sumur. */
  color: string;
  /** Warna panel pilihan tim; dipakai lebih dulu bila ada. */
  panel_color?: string;
  status_label?: string;
  kb_elevation: number | null;
  /** Jalur lubang bor dalam koordinat peta. Kosong bila tanpa survei. */
  path?: [number, number][];
  deviated?: boolean;
  markers: { name: string; md: number; color?: string }[];
}

/**
 * Kisi koordinat survei berikut angkanya, menempel pada tanah.
 *
 * Yang berdiri di sini sebelumnya adalah jaring hiasan setiap 36 piksel,
 * dipasang pada panelnya dan bukan pada petanya: ia tidak ikut bergeser ketika
 * peta digeser dan tidak merapat ketika peta diperbesar, sehingga jaraknya
 * tidak pernah berarti jarak. Yang ini digambar lewat transformasi yang sama
 * dengan sumur dan konturnya, jadi garis 512.000 mT tetap berada di atas
 * 512.000 mT ke mana pun peta dibawa.
 *
 * Angkanya dulu ditiadakan, dengan alasan ruang: peta setinggi 250 piksel di
 * rel samping memang tidak punya tepi untuk ditulisi. Rel itu sekarang
 * setinggi empat ratus dan bisa dilebarkan dengan tangan, dan yang tersisa
 * dari keputusan lama adalah kisi yang berbentuk seperti kisi tanpa pernah
 * menyebut tempat — padahal sumur di Prabumulih tidak disebut orang "yang di
 * kiri atas" melainkan "yang di 512.000 mT". Kisi yang tidak menyebut tempat
 * lebih buruk daripada tidak ada kisi, sebab bentuknya menjanjikan bahwa ia
 * menyebut.
 *
 * Langkahnya dihitung ulang dari **bingkai yang sedang tampak**, bukan dari
 * bingkai pasnya: itulah sebabnya angkanya berubah sendiri ketika peta
 * diperbesar. Lapangan 12 km yang memenuhi panel mendapat garis tiap 5 km;
 * satu gerombolan yang diperbesar sampai selebar 800 m mendapat garis tiap
 * 200 m — kisi yang cacah garisnya tetap sedangkan jaraknya mengikuti apa
 * yang muat.
 *
 * Satu langkah untuk kedua sumbu, sebab petanya setara skala: kisi persegi
 * adalah satu-satunya kisi yang jaraknya boleh dibaca sebagai jarak.
 */
function CoordinateGrid({
  transform,
  width,
  height,
}: {
  transform: MapTransform;
  width: number;
  height: number;
}) {
  if (!(width > 0) || !(height > 0)) return null;

  const [west, north] = transform.toWorld(0, 0);
  const [east, south] = transform.toWorld(width, height);
  if (!Number.isFinite(west) || !Number.isFinite(south)) return null;

  // Empat pembagian: sisi pendek peta ini adalah lebarnya — dua ratus empat
  // puluh piksel di rel yang belum dilebarkan — jadi langkahnya terbaca dari
  // lebar itu, dan tiga pembagian menyisakan satu-dua garis tegak saja.
  // Sebuah easting enam angka memakan tiga puluh lima piksel dari enam puluh
  // yang dibagikan empat pembagian, jadi angkanya tetap muat.
  const step = coordinateStep(Math.min(east - west, north - south), 4);

  // Batang skala memegang sudut kiri atas, dan angka yang tercetak di
  // belakangnya tidak terbaca sebagai angka. Yang dilepas hanya bilangannya —
  // garis kisinya tetap melintas di bawahnya.
  //
  // Sudut itu dulu sudut kiri bawah, dan di panel selebar dua ratus empat puluh
  // piksel batang selebar delapan puluh empat memakan dua perlima tepi bawahnya
  // — yaitu satu-satunya lajur yang cukup panjang untuk easting enam angka.
  // Yang tersisa di sana adalah satu angka easting berhadapan dengan empat
  // northing: sumbu yang bertanda sebelah. Ditukar dengan northing paling utara
  // adalah tukar yang murah.
  const BAR_BOTTOM = 34;

  return (
    <g pointerEvents="none">
      {coordinateTicks(west, east, step).map((value) => {
        const x = transform.toScreen(value, north)[0];
        return (
          <g key={`e${value}`} data-axis="easting">
            <line x1={x} x2={x} y1={0} y2={height}
                  stroke="var(--grid-line)" strokeWidth={1} opacity={0.5} />
            {/* Angka yang separuhnya terpotong tepi panel terbaca sebagai
                angka yang lain — "512.000" yang kehilangan digit pertamanya
                adalah "12.000", dan itu bukan tempat mana pun. */}
            {x > 24 && x < width - 24 && (
              <text x={x} y={height - 6} textAnchor="middle" style={tickLabel}>
                {value.toFixed(0)}
              </text>
            )}
          </g>
        );
      })}
      {coordinateTicks(south, north, step).map((value) => {
        const y = transform.toScreen(east, value)[1];
        return (
          <g key={`n${value}`} data-axis="northing">
            <line x1={0} x2={width} y1={y} y2={y}
                  stroke="var(--grid-line)" strokeWidth={1} opacity={0.5} />
            {y > BAR_BOTTOM && y < height - 14 && (
              <text x={6} y={y - 4} style={tickLabel}>{value.toFixed(0)}</text>
            )}
          </g>
        );
      })}
    </g>
  );
}

export const BASEMAP_LAYERS = [
  { key: "contours", label: "Contours" },
  { key: "paths", label: "Well paths" },
  { key: "traverse", label: "Traverse" },
  { key: "grid", label: "Grid" },
  { key: "outline", label: "Outline" },
] as const;

export type BasemapLayerKey = (typeof BASEMAP_LAYERS)[number]["key"];

export type BasemapLayers = Partial<Record<BasemapLayerKey, boolean>>;

export const BASEMAP_LAYER_DEFAULTS: Record<BasemapLayerKey, boolean> = {
  contours: true,
  paths: true,
  traverse: true,
  grid: true,
  outline: false,
};

/** Baris sakelar layer, dipisah agar pemanggil bisa menaruhnya di kepala panel. */
export function BasemapLayerToggles({
  layers,
  onChange,
}: {
  layers: BasemapLayers;
  onChange: (layers: BasemapLayers) => void;
}) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
      {BASEMAP_LAYERS.map((layer) => {
        const on = layers[layer.key] ?? BASEMAP_LAYER_DEFAULTS[layer.key];
        return (
          <Chip
            key={layer.key}
            active={on}
            onClick={() => onChange({ ...layers, [layer.key]: !on })}
          >
            {layer.label}
          </Chip>
        );
      })}
    </div>
  );
}

/** Legenda status sumur, sekompak yang muat di rel samping. */
export function BasemapLegend() {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "3px 9px" }}>
      {WELL_STATUS_LEGEND.map((entry) => (
        <span key={entry.name} style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: entry.fill === "none" ? "transparent" : entry.fill,
              border: "1.2px solid var(--ink-4)",
              flex: "none",
            }}
          />
          <span style={{ font: "400 9px var(--font-sans)", color: "var(--ink-5)" }}>
            {entry.name}
          </span>
        </span>
      ))}
    </div>
  );
}

export default function Basemap({
  wells,
  activeIds,
  order,
  selectedName = null,
  markerName,
  unitSystem,
  layers = {},
  extent,
  height = 250,
  traverseName = null,
  onPick,
}: {
  wells: BasemapWell[];
  /** Sumur yang tercentang di daftar "Wells correlated". */
  activeIds: Set<number>;
  /** Nama sumur aktif menurut urutan kolom panel, kiri ke kanan. */
  order: string[];
  /** Sumur yang sedang dipilih di seluruh aplikasi, diberi label tebal. */
  selectedName?: string | null;
  /** Marker yang dikonturkan; kosong berarti tanpa kontur. */
  markerName: string;
  unitSystem: string;
  layers?: BasemapLayers;
  /** Batas peta dari server. Bila kosong, dihitung dari kepala sumur. */
  extent?: Extent | null;
  height?: number;
  /** Nama lintasan yang sedang ditampilkan; huruf ujung peta dibaca darinya. */
  traverseName?: string | null;
  onPick?: (well: BasemapWell) => void;
}) {
  const [box, size] = useElementSize<HTMLDivElement>();
  const zoom = useMapView();
  const units = unitsFor(unitSystem);
  /**
   * Sumur yang sedang ditunjuk kursor.
   *
   * Peta yang penuh menjawab pertanyaan "bentuknya bagaimana" dan berhenti di
   * situ: pertanyaan berikutnya selalu tentang satu titik tertentu — yang
   * berdiri sendirian di sayap, atau yang samar tepat di seberang lintasan.
   * Namanya di peta ini tertulis 8 piksel dan berdesakan dengan label kontur;
   * menunjuknya dengan kursor menebalkan yang satu itu, menaikkannya ke atas
   * seluruh gambar, dan menyebutkan tempatnya dengan angka yang sama dengan
   * yang tertulis di tepi kisi.
   */
  const [hovered, setHovered] = useState<number | null>(null);
  // Satu ref gabungan yang tetap identitasnya: ref sebaris melahirkan fungsi
  // baru tiap render, dan React akan melepas lalu memasang ulang pengamat
  // ukuran serta seluruh pendengar peta pada setiap render.
  const attachMap = useCallback(
    (el: HTMLDivElement | null) => {
      box(el);
      zoom.bind(el);
    },
    [box, zoom.bind],
  );
  const on = (key: BasemapLayerKey) => layers[key] ?? BASEMAP_LAYER_DEFAULTS[key];

  const located = useMemo(
    () => wells.filter((w) => w.x !== null && w.y !== null),
    [wells],
  );

  // Batas peta selalu meliputi seluruh sumur proyek, bukan hanya yang aktif:
  // peta yang menskalakan ulang dirinya setiap satu centang berubah membuat
  // dua bacaan berturut-turut tak bisa dibandingkan.
  const bounds = useMemo(
    () => extent ?? extentOf(located),
    [extent, located],
  );

  const fitted = useMemo(
    () => fitTransform(bounds, size.width, size.height, 26),
    [bounds, size.width, size.height],
  );

  // Pandangan pengguna duduk di atas peta yang sudah pas, bukan menggantikannya:
  // batas peta tetap ditentukan sebaran sumur, jadi memperbesar tidak mengubah
  // apa yang dianggap "seluruh lapangan".
  const transform = useMemo(() => applyView(fitted, zoom.view), [fitted, zoom.view]);

  const active = useMemo(
    () => located.filter((w) => activeIds.has(w.id)),
    [located, activeIds],
  );
  const passive = useMemo(
    () => located.filter((w) => !activeIds.has(w.id)),
    [located, activeIds],
  );

  /**
   * Kontur dibangun dari sumur yang dikorelasikan bila jumlahnya cukup, dan
   * dari seluruh sumur bila tidak. Tiga titik kendali adalah batas bawah
   * sebuah permukaan; di bawah itu yang tergambar bukan struktur melainkan
   * hiasan.
   */
  const contourSource = active.length >= 3 ? active : located;
  const control = useMemo(
    () => structureControlPoints(contourSource, markerName),
    [contourSource, markerName],
  );
  // Kisi 56x40 dikalikan jumlah titik kendali; jangan dihitung saat lapisan
  // konturnya sendiri dimatikan.
  const showContours = on("contours");
  const contours = useMemo(
    () =>
      showContours ? structureContours(control, transform, size.width, size.height) : [],
    [showContours, control, transform, size.width, size.height],
  );

  const ends = useMemo(() => traverseEnds(traverseName), [traverseName]);

  const byName = useMemo(() => new Map(active.map((w) => [w.name, w])), [active]);
  const traverse = useMemo(
    () => order.map((name) => byName.get(name)).filter((w): w is BasemapWell => Boolean(w)),
    [order, byName],
  );

  const colourOf = (well: BasemapWell) =>
    well.panel_color || (well.color && well.color !== "none" ? well.color : "var(--accent)");

  if (!located.length) {
    return (
      <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)" }}>
        No well in this project has surface coordinates yet.
      </span>
    );
  }

  const barWidth = 84;

  // Label sumur dan label kontur berbagi satu bidang, dan pada peta selebar
  // 300 px keduanya berebut ruang: "-2160" yang jatuh di atas "A\u2032"
  // terbaca sebagai satu string, bukan dua keterangan. Nama sumur dan tanda
  // lintasan yang menang — kontur masih terbaca dari garisnya, nama sumur
  // tidak punya pengganti — jadi label kontur yang bertabrakan disingkirkan.
  // Lebar dihitung, bukan diukur: IBM Plex Mono maju 0.625 em per aksara.
  const GLYPH = 5;
  const labelBoxes: { x0: number; y0: number; x1: number; y1: number }[] = [];
  if (transform) {
    for (const w of active) {
      const [px, py] = transform.toScreen(w.x!, w.y!);
      // Dari tepi kiri simbolnya sampai ujung nama di kanannya.
      labelBoxes.push({
        x0: px - 6,
        y0: py - 6,
        x1: px + 7 + w.name.length * GLYPH,
        y1: py + 7,
      });
    }
    if (on("traverse") && traverse.length > 1) {
      const [ax, ay] = transform.toScreen(traverse[0].x!, traverse[0].y!);
      const last = traverse[traverse.length - 1];
      const [bx, by] = transform.toScreen(last.x!, last.y!);
      // Huruf ujung berukuran 9 px: 5.6 px per aksara. Lebarnya dihitung dari
      // labelnya sendiri, sebab "A" dan "A\u2032" tidak sama panjang.
      const END_GLYPH = 5.6;
      labelBoxes.push({
        x0: ax - 13,
        y0: ay - 16,
        x1: ax - 12 + ends.start.length * END_GLYPH,
        y1: ay - 5,
      });
      labelBoxes.push({
        x0: bx + 6,
        y0: by - 16,
        x1: bx + 7 + ends.end.length * END_GLYPH,
        y1: by - 5,
      });
    }
  }
  /**
   * Nama sumur tak tercentang yang muat tanpa menimpa nama tetangganya.
   *
   * Sumur samar ada di peta ini supaya bentuk lapangan tidak hilang begitu
   * separuh sumur dimatikan, dan selama ini ia titik tanpa nama: "ada satu
   * sumur di seberang lintasan" adalah setengah keterangan sampai ia bernama,
   * sebab yang hendak memasukkannya ke korelasi perlu tahu bahwa yang
   * ditunggunya di situ adalah KAG-021. Sebuah lapangan 1.451 sumur yang seluruh
   * namanya dicetak sekaligus adalah panel yang tak terbaca, jadi namanya
   * dipasang serakah: sebuah nama dilewati ketika kotaknya bertabrakan dengan
   * nama yang sudah berdiri. Memperbesar merenggangkan titiknya, dan nama
   * muncul sendiri satu per satu — tanpa sakelar yang harus ditemukan lebih
   * dulu. Nama sumur tercentang dan huruf ujung lintasan sudah lebih dulu
   * berdiri di `labelBoxes`, jadi keduanya selalu menang.
   */
  const DIM_GLYPH = 4.7;
  const dimLabelled = new Set<number>();
  if (transform) {
    for (const w of passive) {
      // Batas atas yang murah: empat puluh nama samar sudah lebih banyak
      // daripada yang dibaca siapa pun sekali pandang.
      if (dimLabelled.size >= 40) break;
      const [px, py] = transform.toScreen(w.x!, w.y!);
      if (px < -20 || px > size.width + 20 || py < -12 || py > size.height + 12) continue;
      const kotak = {
        x0: px - 4,
        y0: py - 5,
        x1: px + 6 + w.name.length * DIM_GLYPH,
        y1: py + 6,
      };
      if (labelBoxes.some((b) => kotak.x0 < b.x1 && kotak.x1 > b.x0
                                 && kotak.y0 < b.y1 && kotak.y1 > b.y0)) {
        continue;
      }
      labelBoxes.push(kotak);
      dimLabelled.add(w.id);
    }
  }

  const clearOfLabels = (x: number, y: number, text: string) =>
    !labelBoxes.some(
      (b) =>
        x < b.x1 && x + text.length * GLYPH > b.x0 && y - 6 < b.y1 && y + 2 > b.y0,
    );

  /**
   * Sumur yang sedang ditunjuk, berikut tempatnya di layar. Dicari di antara
   * yang **jatuh di dalam bingkainya**: sumur yang keluar dari panel karena
   * peta digeser tidak boleh meninggalkan keterangan yang menggantung di sudut
   * tentang titik yang tidak ada lagi.
   */
  const hot = (() => {
    if (!transform || hovered === null) return null;
    const well = located.find((w) => w.id === hovered);
    if (!well) return null;
    const at = transform.toScreen(well.x!, well.y!) as [number, number];
    if (at[0] < -2 || at[0] > size.width + 2 || at[1] < -2 || at[1] > size.height + 2) {
      return null;
    }
    return { well, at, aktif: activeIds.has(well.id) };
  })();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <div
        ref={attachMap}
        // Kursor yang meninggalkan kotaknya meninggalkan sorotannya juga:
        // `pointerleave` sebuah sumur tidak selalu terjadi ketika tangan
        // bergerak cepat ke luar panel, dan sorotan yang tertinggal menunjuk
        // sumur yang sudah tidak ditunjuk siapa pun.
        onPointerLeave={() => setHovered(null)}
        style={{
          position: "relative",
          height,
          background: "var(--canvas-deep)",
          border: "1px solid var(--line-strong)",
          borderRadius: 5,
          overflow: "hidden",
          cursor: zoom.panning ? "grabbing" : undefined,
          touchAction: "none",
        }}
      >
        {transform && (
          <svg
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
            aria-label="Correlation basemap"
          >
            {/* Paling dulu digambar, jadi kontur dan sumur berdiri di atasnya. */}
            {on("grid") && (
              <CoordinateGrid transform={transform} width={size.width} height={size.height} />
            )}
            {on("outline") && (
              <rect
                x={14}
                y={12}
                width={Math.max(size.width - 28, 0)}
                height={Math.max(size.height - 24, 0)}
                fill="none"
                stroke="var(--ink-7)"
                strokeWidth={1}
                strokeDasharray="8 5"
              />
            )}

            {contours.map((contour) => (
              <path
                key={contour.level}
                d={contour.d}
                fill="none"
                stroke="var(--accent)"
                strokeOpacity={contour.opacity * 0.8}
                strokeWidth={1}
              />
            ))}
            {contours
              .filter((_, index) => index % 2 === 0)
              .map((contour) => {
                const anchor = contourAnchor(contour);
                if (!anchor) return null;
                const text = contour.level.toFixed(0);
                if (!clearOfLabels(anchor[0], anchor[1], text)) return null;
                return (
                  <text
                    key={`label-${contour.level}`}
                    x={anchor[0]}
                    y={anchor[1]}
                    style={{
                      font: "400 8px var(--font-mono)",
                      fill: "var(--accent)",
                      paintOrder: "stroke",
                      stroke: "var(--canvas-deep)",
                      strokeWidth: 3,
                      strokeLinejoin: "round",
                    }}
                  >
                    {text}
                  </text>
                );
              })}

            {/* Jalur lubang bor. Sumur menyimpang mendarat sampai satu setengah
                kilometer dari deriknya, dan justru itu yang menentukan lapisan
                mana yang ditembusnya — jadi jalurnya digambar untuk sumur yang
                dikorelasikan. */}
            {on("paths") &&
              active
                .filter((w) => w.deviated && (w.path?.length ?? 0) > 1)
                .map((w) => {
                  const points = w.path!.map(([x, y]) => transform.toScreen(x, y));
                  const end = points[points.length - 1];
                  return (
                    <g key={`jalur-${w.id}`}>
                      <polyline
                        points={points.map(([sx, sy]) => `${sx},${sy}`).join(" ")}
                        fill="none"
                        stroke={colourOf(w)}
                        strokeWidth={1.3}
                        strokeLinecap="round"
                        opacity={0.85}
                      />
                      <circle
                        cx={end[0]}
                        cy={end[1]}
                        r={2.2}
                        fill="none"
                        stroke={colourOf(w)}
                        strokeWidth={1}
                      />
                    </g>
                  );
                })}

            {on("traverse") && traverse.length > 1 && (
              <polyline
                points={traverse
                  .map((w) => transform.toScreen(w.x!, w.y!).join(","))
                  .join(" ")}
                fill="none"
                stroke="var(--amber)"
                strokeWidth={1.6}
                strokeDasharray="6 4"
              />
            )}

            {/* Sumur tak tercentang: titik samar bernama, tetap bisa diklik
                untuk memasukkannya ke korelasi. */}
            {passive.map((w) => {
              const [px, py] = transform.toScreen(w.x!, w.y!);
              const lit = hovered === w.id;
              return (
                <g key={`pasif-${w.id}`} data-well={w.id}>
                  {/* Titik samar yang ditunjuk menyala penuh: yang sedang
                      ditanyakan orang tidak boleh menjadi yang paling sulit
                      dibaca justru pada saat ia ditanyakan. */}
                  <circle
                    cx={px}
                    cy={py}
                    r={lit ? 3.5 : 2}
                    fill={lit ? "var(--accent)" : "var(--ink-7)"}
                  />
                  {/* Nama sumur yang sedang ditunjuk digambar belakangan, di
                      atas seluruh peta; di sini ia akan tertimbun kontur dan
                      titik tetangganya justru di gerombolan tempat ia paling
                      dibutuhkan. */}
                  {dimLabelled.has(w.id) && !lit && (
                    <text x={px + 5} y={py + 3} style={dimLabel}>
                      {w.name}
                    </text>
                  )}
                  {/* Sasaran tunjuk lebih besar dari titiknya: menuntut
                      ketepatan dua piksel dari tangan di atas tetikus membuat
                      fitur yang bekerja terasa rusak. */}
                  <circle
                    cx={px}
                    cy={py}
                    r={9}
                    fill="transparent"
                    style={onPick ? { cursor: "pointer" } : undefined}
                    onPointerEnter={() => setHovered(w.id)}
                    onPointerLeave={() =>
                      setHovered((now) => (now === w.id ? null : now))}
                    onClick={onPick ? () => onPick(w) : undefined}
                  >
                    <title>
                      {`${w.name}${w.status_label ? ` — ${w.status_label}` : ""}`
                       + `${onPick ? " — click to add to the correlation" : ""}`}
                    </title>
                  </circle>
                </g>
              );
            })}

            {/* Sumur tercentang: isi peta ini. */}
            {active.map((w) => {
              const [px, py] = transform.toScreen(w.x!, w.y!);
              const isSelected = w.name === selectedName;
              const lit = hovered === w.id;
              const fill = w.color === "none" ? "transparent" : w.color || colourOf(w);
              return (
                <g key={`aktif-${w.id}`} data-well={w.id}>
                  <circle
                    cx={px}
                    cy={py}
                    r={isSelected || lit ? 5 : 4}
                    fill={fill}
                    stroke={isSelected || lit ? "var(--accent)" : colourOf(w)}
                    strokeWidth={isSelected || lit ? 2 : 1.4}
                  />
                  {/* Nama yang sedang ditunjuk naik ke lapisan paling atas,
                      sebab di sini ia berbagi bidang dengan label kontur dan
                      nama tetangganya. */}
                  {!lit && (
                    <text
                      x={px + 7}
                      y={py + 3}
                      style={{
                        font: `${isSelected ? 600 : 500} 8px var(--font-mono)`,
                        fill: isSelected ? "var(--accent)" : "var(--ink-3)",
                        paintOrder: "stroke",
                        stroke: "var(--canvas-deep)",
                        strokeWidth: 3,
                        strokeLinejoin: "round",
                      }}
                    >
                      {w.name}
                    </text>
                  )}
                  <circle
                    cx={px}
                    cy={py}
                    r={9}
                    fill="transparent"
                    style={onPick ? { cursor: "pointer" } : undefined}
                    onPointerEnter={() => setHovered(w.id)}
                    onPointerLeave={() =>
                      setHovered((now) => (now === w.id ? null : now))}
                    onClick={onPick ? () => onPick(w) : undefined}
                  >
                    <title>
                      {`${w.name}${w.status_label ? ` — ${w.status_label}` : ""}`
                       + `${onPick ? " — click to take out of the correlation" : ""}`}
                    </title>
                  </circle>
                </g>
              );
            })}

            {on("traverse") && traverse.length > 1 && (
              <>
                <text
                  x={transform.toScreen(traverse[0].x!, traverse[0].y!)[0] - 12}
                  y={transform.toScreen(traverse[0].x!, traverse[0].y!)[1] - 7}
                  style={{ font: "600 9px var(--font-mono)", fill: "var(--amber)" }}
                >
                  {ends.start}
                </text>
                <text
                  x={transform.toScreen(
                    traverse[traverse.length - 1].x!,
                    traverse[traverse.length - 1].y!,
                  )[0] + 7}
                  y={
                    transform.toScreen(
                      traverse[traverse.length - 1].x!,
                      traverse[traverse.length - 1].y!,
                    )[1] - 7
                  }
                  style={{ font: "600 9px var(--font-mono)", fill: "var(--amber)" }}
                >
                  {ends.end}
                </text>
              </>
            )}

            {/* Sumur yang ditunjuk, di atas segalanya: cincin dan nama tebal
                yang tidak bisa ditimbun kontur maupun tetangganya. */}
            {hot && (
              <g pointerEvents="none">
                <circle
                  cx={hot.at[0]}
                  cy={hot.at[1]}
                  r={10.5}
                  fill="none"
                  stroke="var(--accent)"
                  strokeWidth={1.3}
                />
                <text x={hot.at[0] + 9} y={hot.at[1] + 3} style={litLabel}>
                  {hot.well.name}
                </text>
              </g>
            )}
          </svg>
        )}

        {/* Keterangan sumur yang ditunjuk: nama, statusnya, dan tempatnya —
            yaitu angka yang sama dengan yang tertulis di tepi kisi, sehingga
            keduanya saling menerangkan. Ia mengapung di samping titiknya dan
            bukan di sudut panel: mata yang harus berpindah ke sudut kehilangan
            titik yang sedang ditunjuknya di tengah jalan. */}
        {hot && (
          <div
            data-well-readout={hot.well.id}
            style={{
              position: "absolute",
              // Dibalik ke kiri titik begitu titiknya duduk di separuh kanan
              // panel, supaya keterangannya tidak pernah terpotong tepi.
              ...(hot.at[0] > size.width / 2
                ? { right: Math.max(size.width - hot.at[0] + 13, 4) }
                : { left: hot.at[0] + 13 }),
              // Digantung di bawah titiknya dan bukan di sampingnya, sebab di
              // samping ia menutupi justru nama tebal yang baru saja dinyalakan
              // — kartu yang menyembunyikan sorotan yang memanggilnya. Dibalik
              // ke atas titik di separuh bawah panel, supaya ia tidak pernah
              // terpotong tepi.
              ...(hot.at[1] > height / 2
                ? { bottom: Math.max(height - hot.at[1] + 12, 4) }
                : { top: hot.at[1] + 12 }),
              // Sempit, dan tiap keterangan satu baris sendiri. Satu kartu
              // selebar 180 px yang digantung di tengah panel selebar rel hanya
              // punya seratus piksel di sebelahnya, dan sisanya dipatahkan
              // pembungkus baris di tengah bilangan: "X 512480 · Y" lalu
              // "9942118" pada baris berikutnya adalah koordinat yang harus
              // disambung sendiri oleh pembacanya.
              maxWidth: 150,
              display: "flex",
              flexDirection: "column",
              gap: 1,
              padding: "4px 7px",
              background: "var(--panel)",
              border: "1px solid var(--border-control)",
              borderRadius: "var(--radius)",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.18)",
              pointerEvents: "none",
              zIndex: 3,
            }}
          >
            <span style={{ font: "600 10px var(--font-mono)", color: "var(--ink)" }}>
              {hot.well.name}
            </span>
            <span style={{ font: "400 9px var(--font-sans)", color: "var(--ink-5)" }}>
              {hot.aktif ? "correlated" : "not correlated"}
            </span>
            {hot.well.status_label && (
              <span style={{ font: "400 9px var(--font-sans)", color: "var(--ink-6)" }}>
                {hot.well.status_label}
              </span>
            )}
            <span style={coordLine}>X {hot.well.x!.toFixed(0)}</span>
            <span style={coordLine}>Y {hot.well.y!.toFixed(0)}</span>
          </div>
        )}

        <MapZoomControls
          zoom={zoom.view.zoom}
          home={zoom.home}
          onIn={() => zoom.zoomBy(1.4)}
          onOut={() => zoom.zoomBy(1 / 1.4)}
          onReset={zoom.reset}
          // Sudut kiri atas sekarang milik angka northing paling utara, dan
          // tombol yang duduk di atasnya menutupi justru angka yang paling
          // sering dibaca orang.
          corner="under-compass"
        />

        {/* Panah utara */}
        <div
          style={{
            position: "absolute",
            right: 7,
            top: 7,
            width: 24,
            height: 24,
            display: "grid",
            placeItems: "center",
            background: "var(--panel)",
            border: "1px solid var(--border-control)",
            borderRadius: "50%",
            pointerEvents: "none",
          }}
        >
          <svg width="13" height="13" viewBox="0 0 22 22" aria-label="Utara">
            <path d="M11 3 L14 13 L11 11 L8 13 Z" fill="var(--ink)" />
            <path d="M11 15 v4" stroke="var(--ink-6)" strokeWidth="1.6" />
          </svg>
        </div>

        {/* Skala batang, di sudut kiri atas: tepi bawah peta ini adalah lajur
            easting-nya, dan batang selebar 84 px menutup dua perlimanya. */}
        {transform && (
          <div
            style={{
              position: "absolute",
              left: 8,
              top: 7,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              pointerEvents: "none",
            }}
          >
            <div style={{ display: "flex", height: 5, width: barWidth, border: "1px solid var(--ink-3)" }}>
              <span style={{ flex: 1, background: "var(--ink-3)" }} />
              <span style={{ flex: 1 }} />
              <span style={{ flex: 1, background: "var(--ink-3)" }} />
              <span style={{ flex: 1 }} />
            </div>
            <span style={{ font: "400 8px var(--font-mono)", color: "var(--ink-4)" }}>
              0 — {units.formatDistance(barWidth / transform.scale)}
            </span>
          </div>
        )}
      </div>

      <span style={{ font: "400 9px/1.5 var(--font-mono)", color: "var(--ink-6)" }}>
        {active.length} wells correlated
        {located.length > active.length ? ` · ${located.length - active.length} dimmed` : ""}
        {on("traverse") && traverse.length > 1 ? ` · ${ends.label} through ${traverse.length}` : ""}
        {contours.length
          ? ` · ${markerName} contours every ${contours[0].interval} m from ${control.length} wells${
              contourSource === located && active.length < 3 ? " (whole project)" : ""
            }`
          : markerName
            ? " · contours need three wells carrying this marker"
            : ""}
      </span>
    </div>
  );
}

// Halo di belakang tulisannya, dan itu bukan hiasan: angka dan nama di peta ini
// melintasi garis kisi dan garis kontur, dan aksara yang dipotong garis terbaca
// sebagai aksara yang lain.
const tickLabel: React.CSSProperties = {
  font: "500 9px var(--font-mono)", fill: "var(--ink-4)",
  paintOrder: "stroke", stroke: "var(--canvas-deep)", strokeWidth: 3,
  strokeLinejoin: "round",
};

const dimLabel: React.CSSProperties = {
  font: "400 7.5px var(--font-mono)", fill: "var(--ink-6)",
  paintOrder: "stroke", stroke: "var(--canvas-deep)", strokeWidth: 2.5,
  strokeLinejoin: "round",
};

// Angka yang dipatahkan pembungkus baris terbaca sebagai angka yang lain.
const coordLine: React.CSSProperties = {
  font: "400 9px var(--font-mono)", color: "var(--ink-5)", whiteSpace: "nowrap",
};

const litLabel: React.CSSProperties = {
  font: "600 9px var(--font-mono)", fill: "var(--accent)",
  paintOrder: "stroke", stroke: "var(--canvas-deep)", strokeWidth: 3,
  strokeLinejoin: "round",
};
